/*
 * Cassaforte — código de recuperación (primera experiencia) y sección "Cuenta".
 * Usa los servicios originales de Psono: el código se genera y se cifra en el navegador.
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import datastorePasswordService from "../../services/datastore-password";
import passwordRecoveryCodeService from "../../services/password-recovery-code";
import { getStore } from "../../services/store";
import userService from "../../services/user";
import browserClientService from "../../services/browser-client";
import exportService from "../../services/export";
import { type Billing, setBillingLocale, SubscriptionSection } from "./billing";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = Record<string, any>;

/* Marca en la bóveda (cifrada y sincronizada) que el usuario ya guardó un código */
export async function markRecoveryCreated(): Promise<string> {
	const ds = (await datastorePasswordService.getPasswordDatastore()) as unknown as AnyObj | undefined;
	if (!ds) throw new Error("no datastore");
	const created = new Date().toISOString();
	ds.cf_recovery = { created };
	await datastorePasswordService.saveDatastoreContent(ds as never, [[]]);
	datastorePasswordService.handleDatastoreContentChanged(ds as never);
	return created;
}

/* Ajustes de Cassaforte guardados en la bóveda (cifrados y sincronizados entre dispositivos) */
export async function saveCfSettings(patch: AnyObj): Promise<AnyObj> {
	const ds = (await datastorePasswordService.getPasswordDatastore()) as unknown as AnyObj | undefined;
	if (!ds) throw new Error("no datastore");
	ds.cf_settings = { ...(ds.cf_settings || {}), ...patch };
	await datastorePasswordService.saveDatastoreContent(ds as never, [[]]);
	datastorePasswordService.handleDatastoreContentChanged(ds as never);
	return ds.cf_settings;
}

export const IDLE_OPTIONS = [5, 15, 30, 60, 120];

interface Generated { username: string; recovery_password: string; recovery_words: string }

const XIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 6l12 12M18 6 6 18"/></svg>;

/* ---------- Código de recuperación ---------- */
export const RecoverySetup = ({ startGenerated, onDone, onLater, toast }: {
	startGenerated?: boolean; onDone: (created: string) => void; onLater?: () => void; toast: (m: string) => void;
}) => {
	const { t } = useTranslation();
	const [step, setStep] = useState<"intro" | "show">(startGenerated ? "show" : "intro");
	const [gen, setGen] = useState<Generated | null>(null);
	const [busy, setBusy] = useState(false);
	const [saved, setSaved] = useState(false);
	const [error, setError] = useState("");

	const generate = () => {
		setBusy(true); setError("");
		passwordRecoveryCodeService.recoveryGenerateInformation().then(
			(g) => { setGen(g); setStep("show"); setBusy(false); },
			() => { setBusy(false); setError(t("CF_RECOVERY_ERROR")); },
		);
	};
	React.useEffect(() => { if (startGenerated) generate(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

	const parts = gen ? gen.recovery_password.split("-") : [];
	const textFile = () => gen ? [
		t("CF_RECOVERY_FILE_TITLE"), "",
		`${t("USERNAME")}: ${gen.username}`,
		`${t("CF_RECOVERY_PART1")}: ${parts[0] || ""}`,
		`${t("CF_RECOVERY_PART2")}: ${parts[1] || ""}`,
		`${t("CF_RECOVERY_WORDS")}: ${gen.recovery_words}`, "",
		`${t("CF_RECOVERY_CREATED")}: ${new Date().toLocaleString()}`, "",
		t("CF_RECOVERY_FILE_HOWTO"), "https://vault.cassaforte.app/lost-password.html",
	].join("\n") : "";
	const download = () => {
		const blob = new Blob([textFile()], { type: "text/plain;charset=utf-8" });
		const a = document.createElement("a");
		a.href = URL.createObjectURL(blob); a.download = t("CF_RECOVERY_FILENAME"); a.click();
		setTimeout(() => URL.revokeObjectURL(a.href), 1000);
	};
	const print = () => {
		const w = window.open("", "_blank", "width=640,height=720");
		if (!w) { toast(t("CF_RECOVERY_PRINT_BLOCKED")); return; }
		const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c] as string));
		w.document.write(`<html><head><title>Cassaforte</title></head><body style="font:16px/1.6 system-ui,sans-serif;padding:40px;color:#14282B"><pre style="white-space:pre-wrap;font:16px/1.7 ui-monospace,Menlo,monospace">${esc(textFile())}</pre></body></html>`);
		w.document.close(); w.focus(); w.print();
	};
	const copy = () => {
		navigator.clipboard.writeText(`${parts[0] || ""} ${parts[1] || ""}\n${gen?.recovery_words || ""}`).then(() => toast(t("CF_RECOVERY_COPIED")), () => toast(t("CF_ERROR_COPY")));
	};
	const finish = () => {
		setBusy(true);
		markRecoveryCreated().then((created) => onDone(created), () => { setBusy(false); setError(t("CF_ERROR_SAVE")); });
	};

	return (
		<div className="cf-scrim">
			<div className="cf-sheet" role="dialog" aria-modal="true" aria-labelledby="cfRecTitle">
				<div className="cf-shead">
					{step === "intro" && <>
						<h3 id="cfRecTitle">{t("CF_RECOVERY_TITLE")}</h3>
						<p className="cf-lead">{t("CF_RECOVERY_LEAD")}</p>
					</>}
					{step === "show" && <>
						<h3 id="cfRecTitle">{t("CF_RECOVERY_YOUR_CODE")}</h3>
						<p className="cf-lead">{t("CF_RECOVERY_SHOWN_ONCE")}</p>
					</>}
				</div>
				<div className="cf-sbody">
					{step === "intro" && <ul className="cf-points">
						<li>{t("CF_RECOVERY_POINT1")}</li>
						<li>{t("CF_RECOVERY_POINT2")}</li>
						<li>{t("CF_RECOVERY_POINT3")}</li>
					</ul>}
					{step === "show" && !gen && <p className="cf-muted">{t("CF_RECOVERY_GENERATING")}</p>}
					{step === "show" && gen && <>
						<div className="cf-code" aria-label={t("CF_RECOVERY_YOUR_CODE")}>
							<div><span>{t("CF_RECOVERY_PART1")}</span><b>{parts[0]}</b></div>
							<div><span>{t("CF_RECOVERY_PART2")}</span><b>{parts[1]}</b></div>
						</div>
						<p className="cf-words"><span>{t("CF_RECOVERY_WORDS")}</span>{gen.recovery_words}</p>
						<div className="cf-dactions">
							<button className="cf-btn" type="button" onClick={download}>{t("CF_RECOVERY_DOWNLOAD")}</button>
							<button className="cf-btn" type="button" onClick={print}>{t("CF_RECOVERY_PRINT")}</button>
							<button className="cf-btn" type="button" onClick={copy}>{t("CF_COPY")}</button>
						</div>
						<label className="cf-check"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} /><span>{t("CF_RECOVERY_CONFIRM")}</span></label>
					</>}
					{error && <p className="cf-err">{error}</p>}
				</div>
				<div className="cf-bar">
					{step === "intro" && onLater && <button type="button" className="cf-ghost" onClick={onLater}>{t("CF_RECOVERY_LATER")}</button>}
					{step === "intro" && <button type="button" className="cf-save" disabled={busy} onClick={generate}>{busy ? t("CF_RECOVERY_GENERATING") : t("CF_RECOVERY_GENERATE")}</button>}
					{step === "show" && <button type="button" className="cf-save" disabled={!gen || !saved || busy} onClick={finish}>{t("CF_RECOVERY_DONE")}</button>}
				</div>
			</div>
		</div>
	);
};

/* ---------- Cuenta ---------- */
/* ---------- Exportar ---------- */
const ExportSection = ({ toast }: { toast: (m: string) => void }) => {
	const { t } = useTranslation();
	const [fmt, setFmt] = useState<"json" | "csv">("json");
	const [pw, setPw] = useState("");
	const [busy, setBusy] = useState(false);
	const run = () => {
		setBusy(true);
		exportService.exportDatastore(fmt, false, true, fmt === "json" && pw ? pw : undefined, undefined).then(
			() => { setBusy(false); setPw(""); toast(t("CF_EXP_DONE")); },
			() => { setBusy(false); toast(t("CF_ERROR_SAVE")); },
		);
	};
	return (
		<section className="cf-section">
			<h4>{t("CF_EXP_TITLE")}</h4>
			<p className="cf-muted">{t("CF_EXP_HINT")}</p>
			<div className="cf-seg" role="radiogroup" style={{ marginBottom: 12 }}>
				<button type="button" role="radio" aria-checked={fmt === "json"} aria-pressed={fmt === "json"} onClick={() => setFmt("json")}>{t("CF_EXP_JSON")}</button>
				<button type="button" role="radio" aria-checked={fmt === "csv"} aria-pressed={fmt === "csv"} onClick={() => setFmt("csv")}>{t("CF_EXP_CSV")}</button>
			</div>
			{fmt === "json" && <label className="cf-in"><span>{t("CF_EXP_PW")}</span><input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} /></label>}
			<p className="cf-warn" style={{ marginTop: 6 }}>{fmt === "csv" || !pw ? t("CF_EXP_WARN_PLAIN") : t("CF_EXP_WARN_PW")}</p>
			<div className="cf-dactions"><button type="button" className="cf-btn" disabled={busy} onClick={run}>{busy ? t("CF_EXP_WORKING") : t("CF_EXP_BTN")}</button></div>
		</section>
	);
};

export const AccountPanel = ({ recoveryCreated, onClose, onNewCode, toast, billing, onChoosePlan }: {
	recoveryCreated?: string; onClose: () => void; onNewCode: () => void; toast: (m: string) => void;
	billing: Billing | null; onChoosePlan: () => void;
}) => {
	const { t } = useTranslation();
	const username: string = getStore().getState().user.username || "";
	const [oldPw, setOldPw] = useState("");
	const [newPw, setNewPw] = useState("");
	const [newPw2, setNewPw2] = useState("");
	const [busy, setBusy] = useState(false);
	const [errors, setErrors] = useState<string[]>([]);
	const mismatch = newPw2.length > 0 && newPw !== newPw2;

	const change = (e: React.FormEvent) => {
		e.preventDefault();
		if (busy || mismatch || !oldPw || !newPw) return;
		setBusy(true); setErrors([]);
		userService.saveNewPassword(newPw, newPw2, oldPw).then(
			() => { setBusy(false); setOldPw(""); setNewPw(""); setNewPw2(""); toast(t("CF_PW_CHANGED")); },
			(err: AnyObj) => { setBusy(false); const list = (err && (err.errors || err.non_field_errors)) || ["CF_ERROR_SAVE"]; setErrors(Array.isArray(list) ? list : [String(list)]); },
		);
	};

	return (
		<div className="cf-scrim">
			<form className="cf-sheet" role="dialog" aria-modal="true" aria-labelledby="cfAccTitle" onSubmit={change}>
				<div className="cf-shead">
					<button type="button" className="cf-sx" aria-label={t("CLOSE")} onClick={onClose}>{XIcon}</button>
					<h3 id="cfAccTitle">{t("CF_ACCOUNT")}</h3>
					<p className="cf-lead">{username}</p>
				</div>
				<div className="cf-sbody">
					<SubscriptionSection billing={billing} onChoose={onChoosePlan} toast={toast} />
					<section className="cf-section">
						<h4>{t("CF_RECOVERY_SECTION")}</h4>
						<p className="cf-muted">{recoveryCreated ? t("CF_RECOVERY_HAS", { date: new Date(recoveryCreated).toLocaleDateString() }) : t("CF_RECOVERY_NONE")}</p>
						<button type="button" className="cf-btn" onClick={onNewCode}>{recoveryCreated ? t("CF_RECOVERY_NEW") : t("CF_RECOVERY_GENERATE")}</button>
					</section>
					<section className="cf-section">
						<h4>{t("CF_CHANGE_PW")}</h4>
						<p className="cf-muted">{t("CF_CHANGE_PW_HINT")}</p>
						<label className="cf-in"><span>{t("CF_CURRENT_PW")}</span><input type="password" autoComplete="current-password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} /></label>
						<label className="cf-in"><span>{t("CF_NEW_PW")}</span><input type="password" autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} /></label>
						<label className="cf-in"><span>{t("CF_NEW_PW_REPEAT")}</span><input type="password" autoComplete="new-password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} />
							<small className="cf-err">{mismatch ? t("CF_PW_MISMATCH") : ""}</small></label>
						{errors.map((er) => <p key={er} className="cf-err">{t(er)}</p>)}
					</section>
					<ExportSection toast={toast} />
				</div>
				<div className="cf-bar">
					<button type="button" className="cf-ghost" onClick={onClose}>{t("CLOSE")}</button>
					<button type="submit" className="cf-save" disabled={busy || mismatch || !oldPw || !newPw || !newPw2}>{busy ? t("CF_SAVING") : t("CF_CHANGE_PW")}</button>
				</div>
			</form>
		</div>
	);
};

/* ---------- Ajustes ---------- */
export const SettingsPanel = ({ idleMinutes, onIdleChange, onClose, toast }: {
	idleMinutes: number; onIdleChange: (m: number) => void; onClose: () => void; toast: (m: string) => void;
}) => {
	const { t, i18n } = useTranslation();
	const lang = (i18n.language || "es").startsWith("en") ? "en" : "es";
	const [saving, setSaving] = useState(false);
	const setLang = (lng: string) => {
		if (lng === lang) return;
		i18n.changeLanguage(lng).then(() => {
			browserClientService.emitSec("language-changed", lng, () => {});
			userService.saveNewLanguage(lng).then(() => toast(t("CF_SETTINGS_SAVED")), () => toast(t("CF_ERROR_SAVE")));
			setBillingLocale(lng);
		});
	};
	const setIdle = (m: number) => {
		if (m === idleMinutes || saving) return;
		setSaving(true);
		saveCfSettings({ idle_minutes: m }).then(
			() => { setSaving(false); onIdleChange(m); toast(t("CF_SETTINGS_SAVED")); },
			() => { setSaving(false); toast(t("CF_ERROR_SAVE")); },
		);
	};
	return (
		<div className="cf-scrim">
			<div className="cf-sheet" role="dialog" aria-modal="true" aria-labelledby="cfSetTitle">
				<div className="cf-shead">
					<button type="button" className="cf-sx" aria-label={t("CLOSE")} onClick={onClose}>{XIcon}</button>
					<h3 id="cfSetTitle">{t("SETTINGS")}</h3>
					<p className="cf-lead">{t("CF_SETTINGS_LEAD")}</p>
				</div>
				<div className="cf-sbody">
					<section className="cf-section">
						<h4>{t("CF_PREFERRED_LANGUAGE")}</h4>
						<p className="cf-muted">{t("CF_PREFERRED_LANGUAGE_HINT")}</p>
						<div className="cf-seg" role="radiogroup">
							<button type="button" role="radio" aria-checked={lang === "es"} aria-pressed={lang === "es"} onClick={() => setLang("es")}>Español</button>
							<button type="button" role="radio" aria-checked={lang === "en"} aria-pressed={lang === "en"} onClick={() => setLang("en")}>English</button>
						</div>
					</section>
					<section className="cf-section">
						<h4>{t("CF_IDLE_TITLE")}</h4>
						<p className="cf-muted">{t("CF_IDLE_HINT")}</p>
						<div className="cf-toggles" role="radiogroup">
							{IDLE_OPTIONS.map((m) => (
								<button key={m} type="button" className="cf-tg" role="radio" aria-checked={m === idleMinutes} aria-pressed={m === idleMinutes} disabled={saving} onClick={() => setIdle(m)}>
									{m < 60 ? t("CF_MINUTES", { n: m }) : t("CF_HOURS", { count: m / 60, n: m / 60 })}
								</button>))}
						</div>
					</section>
				</div>
				<div className="cf-bar"><button type="button" className="cf-save" onClick={onClose}>{t("CF_DONE")}</button></div>
			</div>
		</div>
	);
};
